/**
 * LogStorageManager - Storage statistics and log deletion tool
 * 
 * Displays current log storage size and provides flexible deletion options.
 * Placed in the LogViewerPage header for easy access.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Database, 
  Trash2, 
  RefreshCw, 
  AlertTriangle, 
  X, 
  ChevronDown,
  Clock,
  HardDrive,
  CheckCircle2
} from 'lucide-react';

interface StorageStats {
  totalLogs: number;
  totalSizeBytes: number;
  totalSizeMB: string;
  oldestLog: string | null;
  newestLog: string | null;
  breakdown: {
    last24h: number;
    last7d: number;
    last30d: number;
    older: number;
  };
}

interface LogStorageManagerProps {
  onDeleteComplete?: () => void;
}

type DeleteType = 'all' | 'older_than' | 'by_level' | 'by_time_range' | 'all_realms';

interface RealmLogCount {
  realm_id: string;
  log_count: number;
}

export function LogStorageManager({ onDeleteComplete }: LogStorageManagerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState<StorageStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<{ success: boolean; deleted: number; error?: string } | null>(null);

  // Delete options state
  const [deleteType, setDeleteType] = useState<DeleteType>('older_than');
  const [olderThanDays, setOlderThanDays] = useState(30);
  const [selectedLevels, setSelectedLevels] = useState<string[]>(['debug']);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [realms, setRealms] = useState<RealmLogCount[]>([]);
  const [targetRealmId, setTargetRealmId] = useState<string>('');

  // Fetch storage stats
  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setStatsError(null);
    try {
      const token = localStorage.getItem('webelieve_access_token');
      const response = await fetch('/api/logs/storage/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStatsError(errorData.error || `Failed to load stats (${response.status})`);
      }
    } catch (error) {
      console.error('[LogStorageManager] Failed to fetch stats:', error);
      setStatsError('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch realms with log counts
  const fetchRealms = useCallback(async () => {
    try {
      const token = localStorage.getItem('webelieve_access_token');
      const response = await fetch('/api/logs/realms', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRealms(data);
      }
    } catch {
      // Non-fatal — realm list is optional
    }
  }, []);

  // Fetch stats and realms when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchStats();
      fetchRealms();
    }
  }, [isOpen, fetchStats, fetchRealms]);

  // Handle delete
  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteResult(null);
    
    try {
      const token = localStorage.getItem('webelieve_access_token');
      const body: Record<string, unknown> = {
        deleteType,
        confirmDelete: true,
      };

      if (targetRealmId) {
        body.targetRealmId = targetRealmId;
      }

      if (deleteType === 'older_than') {
        body.olderThanDays = olderThanDays;
      } else if (deleteType === 'by_level') {
        body.levels = selectedLevels;
      }

      const response = await fetch('/api/logs/delete', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      setDeleteResult({
        success: data.success,
        deleted: data.deleted,
        error: data.error,
      });

      if (data.success && data.deleted > 0) {
        // Refresh stats after successful deletion
        await fetchStats();
        onDeleteComplete?.();
      }
    } catch (error) {
      setDeleteResult({
        success: false,
        deleted: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsDeleting(false);
      setShowConfirmation(false);
    }
  };

  // Get estimated count for current selection
  const getEstimatedCount = (): number => {
    if (!stats) return 0;
    
    switch (deleteType) {
      case 'all_realms':
        return realms.reduce((sum, r) => sum + r.log_count, 0);
      case 'all': {
        if (targetRealmId) {
          const realm = realms.find(r => r.realm_id === targetRealmId);
          return realm?.log_count ?? stats.totalLogs;
        }
        return stats.totalLogs;
      }
      case 'older_than':
        if (olderThanDays >= 30) return stats.breakdown.older;
        if (olderThanDays >= 7) return stats.breakdown.older + stats.breakdown.last30d;
        if (olderThanDays >= 1) return stats.breakdown.older + stats.breakdown.last30d + stats.breakdown.last7d;
        // Less than 1 day: estimate total minus proportional recent logs
        return Math.max(0, stats.totalLogs - Math.round(stats.breakdown.last24h * olderThanDays));
      case 'by_level':
        // Rough estimate - we don't have level breakdown in stats
        return Math.round(stats.totalLogs * (selectedLevels.length / 5));
      default:
        return 0;
    }
  };

  // Format size for display
  const formatSize = (mb: string): string => {
    const num = parseFloat(mb);
    if (num >= 1024) {
      return `${(num / 1024).toFixed(2)} GB`;
    }
    return `${mb} MB`;
  };

  // Format date for display
  const formatDate = (isoString: string | null): string => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        title="Log Storage Manager"
      >
        <Database className="w-4 h-4" />
        <span className="hidden sm:inline">Storage</span>
        {stats && (
          <span className="text-xs font-mono bg-gray-300 dark:bg-gray-600 px-1.5 py-0.5 rounded">
            {formatSize(stats.totalSizeMB)}
          </span>
        )}
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="absolute right-0 top-full mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-lex-accent" />
                <span className="font-medium text-gray-900 dark:text-gray-100">Log Storage</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchStats}
                  disabled={isLoading}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Refresh stats"
                >
                  <RefreshCw className={`w-4 h-4 text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Stats Section */}
            {isLoading && !stats && (
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 py-4">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading storage stats...</span>
                </div>
              </div>
            )}
            
            {statsError && (
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>{statsError}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Stats unavailable due to large dataset. You can still delete logs below.
                </p>
              </div>
            )}
            
            {stats && (
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Logs (est.)</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {stats.totalLogs.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Storage Size</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {formatSize(stats.totalSizeMB)}
                    </div>
                  </div>
                </div>

                {/* Latest log timestamp */}
                {stats.newestLog && (
                  <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>Latest: </span>
                    <span className="font-mono">{formatDate(stats.newestLog)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Delete Section */}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Trash2 className="w-4 h-4 text-red-500" />
                <span className="font-medium text-gray-900 dark:text-gray-100">Delete Logs</span>
              </div>

              {/* Delete Type Selection */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Delete Type</label>
                  <select
                    value={deleteType}
                    onChange={(e) => setDeleteType(e.target.value as DeleteType)}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
                  >
                    <option value="older_than">Older than X days</option>
                    <option value="by_level">By log level</option>
                    <option value="all">Delete logs for a realm</option>
                    <option value="all_realms">Delete ALL logs (all realms)</option>
                  </select>

                  {/* Realm selector — shown for single-realm delete */}
                  {deleteType === 'all' && realms.length > 0 && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                        Target Realm
                      </label>
                      <select
                        value={targetRealmId}
                        onChange={(e) => setTargetRealmId(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
                      >
                        <option value="">My realm (default)</option>
                        {realms.map((r) => (
                          <option key={r.realm_id} value={r.realm_id}>
                            {r.realm_id} ({r.log_count.toLocaleString()} logs)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Conditional options */}
                {deleteType === 'older_than' && (
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                      Delete logs older than
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={olderThanDays}
                        onChange={(e) => setOlderThanDays(parseInt(e.target.value) || 1)}
                        className="w-20 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-lex-accent"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">days</span>
                    </div>
                  </div>
                )}

                {deleteType === 'by_level' && (
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                      Select levels to delete
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['debug', 'info', 'warn', 'error', 'performance'].map((level) => (
                        <label
                          key={level}
                          className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded cursor-pointer transition-colors ${
                            selectedLevels.includes(level)
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedLevels.includes(level)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedLevels([...selectedLevels, level]);
                              } else {
                                setSelectedLevels(selectedLevels.filter((l) => l !== level));
                              }
                            }}
                            className="sr-only"
                          />
                          {level}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Estimated count */}
                <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 px-3 py-2 rounded">
                  Estimated: <span className="font-semibold text-red-600 dark:text-red-400">
                    ~{getEstimatedCount().toLocaleString()}
                  </span> logs will be deleted
                </div>

                {/* Delete Result */}
                {deleteResult && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${
                    deleteResult.success
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                  }`}>
                    {deleteResult.success ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Deleted {deleteResult.deleted.toLocaleString()} logs
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-4 h-4" />
                        {deleteResult.error || 'Delete failed'}
                      </>
                    )}
                  </div>
                )}

                {/* Quick action for 1 hour */}
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => {
                      setDeleteType('older_than');
                      setOlderThanDays(0.0417); // ~1 hour
                      setShowConfirmation(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                    title="Deletes all logs older than 1 hour, keeping only the most recent hour"
                  >
                    <Trash2 className="w-3 h-3" />
                    Keep Only Last Hour
                  </button>
                </div>

                {/* Delete Button / Confirmation */}
                {!showConfirmation ? (
                  <button
                    onClick={() => setShowConfirmation(true)}
                    disabled={deleteType === 'by_level' && selectedLevels.length === 0}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Logs
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded text-sm">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      <span>This action cannot be undone. Are you sure?</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowConfirmation(false)}
                        className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        {isDeleting ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" />
                            Confirm Delete
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default LogStorageManager;
